import { api } from "../../../api/Middleware";
import {
  API_URL,
  MARKETPLACE_LEADS_URL,
  MARKETPLACE_LEADS_DETAIL_URL,
  MARKETPLACE_LEADS_EXPORT_URL,
  MARKETPLACE_LEADS_STATS_URL,
} from "../../../api/Urls";

// =============================
// MARKETPLACE LEADS SERVICES
// =============================

class MarketplaceLeadsServices {
  // =============================
  // LEADS API METHODS
  // =============================

  // Consultar leads da conta
  static async getLeads(params = {}) {
    try {
      const queryString = Object.keys(params)
        .filter(key => params[key] !== undefined && params[key] !== null)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&');
        
      const url = `${API_URL}marketplace/leads/`;
      const fullUrl = queryString ? `${url}?${queryString}` : url;
      return await api.get(fullUrl);
    } catch (error) {
      console.error('Erro ao buscar leads:', error);
      throw error;
    }
  }

  // Atualizar status de lead
  static async updateLeadStatus(leadId, status, notes = "") {
    try {
      const data = {
        lead_id: parseInt(leadId),
        status: status
      };
      
      if (notes && notes.trim()) {
        data.notes = notes.trim();
      }

      const url = `${API_URL}marketplace/leads/`;
      return await api.post(url, data);
    } catch (error) {
      console.error('Erro ao atualizar status do lead:', error);
      throw error;
    }
  }

  // =============================
  // CRUD OPERATIONS (LEGACY)
  // =============================

  // Listar leads com filtros e paginação
  static async listLeads(params = {}) {
    try {
      const queryString = Object.keys(params)
        .filter(key => params[key] !== undefined && params[key] !== null)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&');
        
      const url = queryString ? `${MARKETPLACE_LEADS_URL}?${queryString}` : MARKETPLACE_LEADS_URL;
      return await api.get(url);
    } catch (error) {
      console.error('Erro ao buscar leads do marketplace:', error);
      throw error;
    }
  }

  // Buscar lead específico
  static async getLead(id) {
    try {
      return await api.get(MARKETPLACE_LEADS_DETAIL_URL(id));
    } catch (error) {
      console.error('Erro ao buscar lead do marketplace:', error);
      throw error;
    }
  }

  // Atualizar lead (PATCH)
  static async updateLead(id, data) {
    try {
      return await api.patch(MARKETPLACE_LEADS_DETAIL_URL(id), data);
    } catch (error) {
      console.error('Erro ao atualizar lead do marketplace:', error);
      throw error;
    }
  }

  // =============================
  // EXPORT AND STATS
  // =============================

  // Exportar leads para CSV
  static async exportLeads(params = {}) {
    try {
      const queryString = Object.keys(params)
        .filter(key => params[key] !== undefined && params[key] !== null)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&');
        
      const url = queryString ? `${MARKETPLACE_LEADS_EXPORT_URL}?${queryString}` : MARKETPLACE_LEADS_EXPORT_URL;
      return await api.get(url, {
        responseType: 'blob'
      });
    } catch (error) {
      console.error('Erro ao exportar leads do marketplace:', error);
      throw error;
    }
  }

  // Obter estatísticas dos leads
  static async getLeadsStats(params = {}) {
    try {
      const queryString = Object.keys(params)
        .filter(key => params[key] !== undefined && params[key] !== null)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&');
        
      const url = queryString ? `${MARKETPLACE_LEADS_STATS_URL}?${queryString}` : MARKETPLACE_LEADS_STATS_URL;
      return await api.get(url);
    } catch (error) {
      console.error('Erro ao buscar estatísticas de leads:', error);
      throw error;
    }
  }

  // =============================
  // STATUS MANAGEMENT
  // =============================

  // Atualizar status do lead
  static async updateLeadStatus(id, status, notes = "") {
    try {
      const data = { status };
      if (notes && notes.trim()) {
        data.notes = notes.trim();
      }
      return await MarketplaceLeadsServices.updateLead(id, data);
    } catch (error) {
      console.error('Erro ao atualizar status do lead:', error);
      throw error;
    }
  }

  // =============================
  // HELPER FUNCTIONS
  // =============================

  // Construir parâmetros para API
  static buildSearchParams(filters = {}) {
    const params = {};
    
    // Filtros da API
    if (filters.agent_id && filters.agent_id !== 'all') {
      params.agent_id = filters.agent_id;
    }
    
    if (filters.status && filters.status !== 'all') {
      // Status válidos: new, contacted, qualified, converted, lost, invalid
      const validStatuses = ['new', 'contacted', 'qualified', 'converted', 'lost', 'invalid'];
      if (validStatuses.includes(filters.status)) {
        params.status = filters.status;
      }
    }
    
    // Paginação
    if (filters.limit) {
      params.limit = parseInt(filters.limit);
    }
    
    // Filtro por data (formato YYYY-MM-DD)
    if (filters.date_from) {
      params.date_from = filters.date_from;
    }
    
    return params;
  }

  // =============================
  // HELPER FUNCTIONS (LEGACY)
  // =============================

  // Construir parâmetros de busca
  static buildSearchParams(filters = {}) {
    const params = {};
    
    // Filtros básicos
    if (filters.status && filters.status !== 'all') {
      params.status = filters.status;
    }
    
    if (filters.person_type && filters.person_type !== 'all') {
      params.person_type = filters.person_type;
    }
    
    if (filters.source_page && filters.source_page !== 'all') {
      params.source_page = filters.source_page;
    }
    
    if (filters.search && filters.search.trim()) {
      params.search = filters.search.trim();
    }
    
    // Filtros por ID
    if (filters.agent) {
      params.agent = filters.agent;
    }
    
    if (filters.seller_store) {
      params.seller_store = filters.seller_store;
    }
    
    if (filters.category) {
      params.category = filters.category;
    }
    
    // Filtros de data
    if (filters.date_from) {
      params.date_from = filters.date_from;
    }
    
    if (filters.date_to) {
      params.date_to = filters.date_to;
    }
    
    // Paginação
    if (filters.page) {
      params.page = filters.page;
    }
    
    if (filters.page_size) {
      params.page_size = filters.page_size;
    }
    
    return params;
  }

  // Formatar dados do lead
  static formatLeadData(lead) {
    if (!lead) return null;

    return {
      ...lead,
      formattedCreatedAt: lead.created_at 
        ? new Date(lead.created_at).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })
        : '',
      formattedUpdatedAt: lead.updated_at
        ? new Date(lead.updated_at).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })
        : '',
      formattedContactedAt: lead.contacted_at
        ? new Date(lead.contacted_at).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })
        : null,
      formattedConvertedAt: lead.converted_at
        ? new Date(lead.converted_at).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })
        : null,
      statusVariant: MarketplaceLeadsServices.getStatusVariant(lead.status),
      statusText: MarketplaceLeadsServices.getStatusText(lead.status),
      personTypeText: MarketplaceLeadsServices.getPersonTypeText(lead.person_type),
      sourcePageText: MarketplaceLeadsServices.getSourcePageText(lead.source_page),
      canUpdate: MarketplaceLeadsServices.canUpdateStatus(lead.status),
      nextStatuses: MarketplaceLeadsServices.getNextStatuses(lead.status),
    };
  }

  // Obter variante de cor do status
  static getStatusVariant(status) {
    const variants = {
      new: "primary",
      contacted: "info",
      qualified: "warning",
      converted: "success",
      lost: "danger",
      invalid: "secondary"
    };
    return variants[status] || "secondary";
  }

  // Obter texto do status
  static getStatusText(status) {
    const texts = {
      new: "Novo",
      contacted: "Contatado",
      qualified: "Qualificado",
      converted: "Convertido",
      lost: "Perdido",
      invalid: "Inválido"
    };
    return texts[status] || status;
  }

  // Obter texto do tipo de pessoa
  static getPersonTypeText(personType) {
    const texts = {
      PF: "Pessoa Física",
      PJ: "Pessoa Jurídica"
    };
    return texts[personType] || personType;
  }

  // Obter texto da página de origem
  static getSourcePageText(sourcePage) {
    const texts = {
      agent_detail: "Página do Agente",
      marketplace_list: "Lista do Marketplace",
      category_page: "Página da Categoria",
      search_results: "Resultados de Busca",
      home_page: "Página Inicial"
    };
    return texts[sourcePage] || sourcePage;
  }

  // Verificar se pode atualizar status
  static canUpdateStatus(status) {
    // Estados finais não podem ser alterados
    return !['converted', 'lost', 'invalid'].includes(status);
  }

  // Obter próximos status possíveis baseado no workflow
  static getNextStatuses(currentStatus) {
    const workflow = {
      new: ['contacted', 'qualified', 'invalid'],
      contacted: ['qualified', 'converted', 'lost'],
      qualified: ['converted', 'lost'],
      converted: [], // Estado final
      lost: [], // Estado final
      invalid: [] // Estado final
    };
    
    return workflow[currentStatus] || [];
  }

  // Validar transição de status
  static validateStatusTransition(currentStatus, newStatus) {
    const nextStatuses = MarketplaceLeadsServices.getNextStatuses(currentStatus);
    return nextStatuses.includes(newStatus);
  }

  // =============================
  // UTILITY HELPERS
  // =============================

  // Obter mensagem de erro amigável
  static getErrorMessage(error) {
    if (error.response?.status === 403) {
      return "Você não tem permissão para acessar este recurso.";
    }
    
    if (error.response?.status === 400) {
      const data = error.response.data;
      if (data.non_field_errors) {
        return data.non_field_errors[0];
      }
      
      // Retornar primeiro erro encontrado
      const errorMessages = Object.values(data).flat();
      return errorMessages[0] || "Dados inválidos";
    }

    if (error.response?.status === 404) {
      return "Lead não encontrado";
    }

    if (error.response?.status >= 500) {
      return "Erro interno do servidor. Tente novamente mais tarde.";
    }

    return error.message || "Erro desconhecido";
  }

  // Download de arquivo CSV
  static downloadCSV(blob, filename = null) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Extrair nome do arquivo do cabeçalho Content-Disposition se disponível
    if (!filename) {
      filename = `leads_marketplace_${new Date().toISOString().slice(0, 10)}.csv`;
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  // Formatar estatísticas para exibição
  static formatStatsData(stats) {
    if (!stats) return null;

    const total = stats.total_leads || 0;
    
    return {
      ...stats,
      conversionPercentage: total > 0 ? ((stats.converted_leads || 0) / total * 100).toFixed(1) : '0',
      formattedConversionRate: stats.conversion_rate ? `${stats.conversion_rate}%` : '0%',
      statusDistribution: {
        new: {
          count: stats.new_leads || 0,
          percentage: total > 0 ? ((stats.new_leads || 0) / total * 100).toFixed(1) : '0'
        },
        contacted: {
          count: stats.contacted_leads || 0,
          percentage: total > 0 ? ((stats.contacted_leads || 0) / total * 100).toFixed(1) : '0'
        },
        qualified: {
          count: stats.qualified_leads || 0,
          percentage: total > 0 ? ((stats.qualified_leads || 0) / total * 100).toFixed(1) : '0'
        },
        converted: {
          count: stats.converted_leads || 0,
          percentage: total > 0 ? ((stats.converted_leads || 0) / total * 100).toFixed(1) : '0'
        },
        lost: {
          count: stats.lost_leads || 0,
          percentage: total > 0 ? ((stats.lost_leads || 0) / total * 100).toFixed(1) : '0'
        },
        invalid: {
          count: stats.invalid_leads || 0,
          percentage: total > 0 ? ((stats.invalid_leads || 0) / total * 100).toFixed(1) : '0'
        }
      }
    };
  }

  // Buscar leads com filtros avançados
  static async searchLeads(searchOptions = {}) {
    try {
      const {
        query = '',
        status = 'all',
        personType = 'all',
        sourcePage = 'all',
        agent = null,
        sellerStore = null,
        category = null,
        dateFrom = null,
        dateTo = null,
        page = 1,
        pageSize = 20
      } = searchOptions;

      const params = {
        page,
        page_size: pageSize,
      };

      if (query.trim()) {
        params.search = query.trim();
      }

      if (status && status !== 'all') {
        params.status = status;
      }

      if (personType && personType !== 'all') {
        params.person_type = personType;
      }

      if (sourcePage && sourcePage !== 'all') {
        params.source_page = sourcePage;
      }

      if (agent) {
        params.agent = agent;
      }

      if (sellerStore) {
        params.seller_store = sellerStore;
      }

      if (category) {
        params.category = category;
      }

      if (dateFrom) {
        params.date_from = dateFrom;
      }

      if (dateTo) {
        params.date_to = dateTo;
      }

      return await MarketplaceLeadsServices.listLeads(params);
    } catch (error) {
      console.error('Erro na busca de leads:', error);
      throw error;
    }
  }
}

export default MarketplaceLeadsServices;